import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, Typography, CircularProgress, Alert, Button, Link } from '@mui/material';
import {
  CognitoService,
  getOAuthSignupData,
  clearOAuthSignupData,
} from '../../services/api/auth/cognito.service';
import { cognitoOAuthEndpoint } from '../../services/api/endpoints/cognito-oauth.endpoint';
import { organizationEndpoints } from '../../services/api/endpoints/organisation.endpoint';
import { logger } from '../../utils/logger';
import { AppDispatch } from '../../store';
import { loginUser } from '../../store/slices/authSlice';
import { setSelectedOrganizationId } from '../../store/slices/organizationContextSlice';
import { setSelectedOrganisation } from '../../store/slices/organizationSlice';
import logo from '../../assets/images/cacxi-white-logo.svg';

type CallbackStatus = 'processing' | 'success' | 'error' | 'signup_required' | 'pending_activation';

const AuthCallback = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [message, setMessage] = useState('Processing your sign-in...');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Wait for Amplify to process the OAuth callback
        const result = await CognitoService.handleOAuthCallback();

        if (!result) {
          // No user found - might be a failed OAuth or cancelled
          setStatus('error');
          setError('Sign-in was cancelled or failed. Please try again.');
          setTimeout(() => navigate('/'), 3000);
          return;
        }

        const { user, role, clientId, isNewUser } = result;

        console.log('[AuthCallback] OAuth result:', {
          email: user.email,
          role,
          clientId,
          isNewUser,
          hasClientId: !!clientId,
        });

        // Check if there's signup data stored (user came from signup flow)
        const signupData = getOAuthSignupData();

        if (signupData) {
          // User came from signup flow - create organization
          setMessage('Creating your organization...');

          try {
            // Create organization request with OAuth user's email
            const response = await organizationEndpoints.requestOrganisation({
              companyName: signupData.companyName,
              email: user.email,
              firstName: user.firstName || signupData.firstName,
              planType: signupData.planType,
              planId: signupData.planId,
              isTrailEnabled: signupData.isTrialEnabled,
              isOAuthUser: true, // Flag to indicate OAuth user
            });

            clearOAuthSignupData();

            if (response && response.id) {
              // Update Cognito user attributes with the new clientId and verify email
              await cognitoOAuthEndpoint.updateUserAttributes(
                user.email,
                response.id,
                'client_admin',
                true // Mark email as verified for OAuth users
              );

              // Store signup flow context so ActivateView knows this is an OAuth direct flow
              sessionStorage.setItem('signup_flow', 'direct');
              sessionStorage.setItem('signup_is_oauth', 'true');

              // Redirect to activation page
              setStatus('success');
              setMessage('Organization created! Redirecting to setup...');
              setTimeout(() => {
                navigate(`/activate/${response.id}`, { replace: true });
              }, 1500);
            } else if (response && response.checkoutUrl) {
              // Paid plan - redirect to Stripe
              window.location.href = response.checkoutUrl;
            } else {
              throw new Error('Failed to create organization');
            }
          } catch (orgError) {
            logger.error('Failed to create organization:', orgError);
            clearOAuthSignupData();
            setStatus('error');
            setError('Failed to create organization. Please try again.');
            setTimeout(() => navigate('/signup'), 3000);
          }
          return;
        }

        // User came from login flow
        if (isNewUser || !clientId) {
          // Check if user is coming from activation page
          const returnUrl = sessionStorage.getItem('oauth_return_url');
          if (returnUrl && returnUrl.startsWith('/activate/')) {
            // User signed in from activation page - redirect back there
            sessionStorage.removeItem('oauth_return_url');
            setStatus('success');
            setMessage('Signed in successfully! Returning to activation...');
            setTimeout(() => {
              window.location.href = returnUrl;
            }, 1000);
            return;
          }

          // OAuth user without organization - redirect to signup
          // Store their Google info so they don't have to re-enter it
          setStatus('signup_required');
          setMessage('No organization found. Redirecting to complete your signup...');

          // Store the user's info for the signup page
          sessionStorage.setItem(
            'oauth_user_info',
            JSON.stringify({
              email: user.email,
              firstName: user.firstName,
              userName: user.userName,
              isGoogleUser: true,
            })
          );

          // Don't sign out - keep them authenticated with Google
          // They just need to complete the organization setup
          setTimeout(() => navigate('/signup?oauth=google'), 2000);
          return;
        }

        // Existing user with organization - check organization status first
        setMessage('Checking your account...');

        try {
          // Fetch organization to check status
          const orgs = await organizationEndpoints.getActiveteOrganisations(clientId);
          const org = orgs?.[0];

          if (org && org.status === 'pending') {
            // Organization is pending - user needs to complete activation
            setStatus('pending_activation');
            setMessage('Your account is pending activation.');

            // Sign out since they can't access the dashboard yet
            try {
              await CognitoService.signOut();
              localStorage.clear();
            } catch (signOutError) {
              logger.warn('Sign out warning:', signOutError);
            }
            return;
          }

          // Organization is active - complete login
          setMessage('Signing you in...');

          // Set organization context and full organization object
          dispatch(setSelectedOrganizationId(clientId));
          dispatch(setSelectedOrganisation(org));

          // The user is already signed in via OAuth, we need to update Redux state
          // We'll use a special action or directly set the state
          setStatus('success');
          setMessage('Sign-in successful! Redirecting...');

          // Navigate to dashboard - the ProtectedRoute will handle auth check
          setTimeout(() => {
            // Force a page reload to ensure auth state is properly initialized
            window.location.href = '/dashboard';
          }, 1000);
        } catch (orgError) {
          logger.error('Failed to check organization status:', orgError);
          // If we can't check status, try to fetch org data and continue to dashboard
          setMessage('Signing you in...');

          try {
            // Attempt to fetch organization data even if status check failed
            const orgs = await organizationEndpoints.getActiveteOrganisations(clientId);
            const org = orgs?.[0];

            dispatch(setSelectedOrganizationId(clientId));
            if (org) {
              dispatch(setSelectedOrganisation(org));
            }
          } catch (fetchError) {
            logger.error('Failed to fetch organization:', fetchError);
            // Just set the ID even if we can't get org data
            dispatch(setSelectedOrganizationId(clientId));
          }

          setStatus('success');
          setMessage('Sign-in successful! Redirecting...');
          setTimeout(() => {
            window.location.href = '/dashboard';
          }, 1000);
        }
      } catch (err) {
        logger.error('OAuth callback error:', err);
        setStatus('error');
        setError(err instanceof Error ? err.message : 'An error occurred during sign-in');
        setTimeout(() => navigate('/'), 3000);
      }
    };

    handleCallback();
  }, [navigate, dispatch]);

  return (
    <Box
      className="h-screen flex flex-col items-center justify-center font-sans login-bg"
      sx={{ gap: 3 }}
    >
      <img className="w-32 mb-4" src={logo} alt="CACXI Logo" />

      {status === 'processing' && (
        <>
          <CircularProgress size={48} sx={{ color: '#a855f7' }} />
          <Typography variant="h6" sx={{ color: 'white', mt: 2 }}>
            {message}
          </Typography>
        </>
      )}

      {status === 'success' && (
        <>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: 'rgba(34, 197, 94, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ fontSize: 32 }}>&#10003;</Typography>
          </Box>
          <Typography variant="h6" sx={{ color: 'white', mt: 2 }}>
            {message}
          </Typography>
        </>
      )}

      {status === 'signup_required' && (
        <>
          <Alert
            severity="info"
            sx={{
              maxWidth: 400,
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              color: 'white',
              border: '1px solid rgba(59, 130, 246, 0.3)',
            }}
          >
            {message}
          </Alert>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mt: 1 }}>
            Redirecting to signup page...
          </Typography>
        </>
      )}

      {status === 'error' && (
        <>
          <Alert
            severity="error"
            sx={{
              maxWidth: 400,
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              color: '#fca5a5',
              border: '1px solid rgba(239, 68, 68, 0.3)',
            }}
          >
            {error}
          </Alert>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mt: 1 }}>
            Redirecting...
          </Typography>
        </>
      )}

      {status === 'pending_activation' && (
        <Box
          sx={{
            maxWidth: 480,
            width: '100%',
            mx: 2,
            backgroundColor: '#21172B',
            border: '1px solid #291c35',
            borderRadius: '24px',
            px: 5,
            py: 5,
            textAlign: 'center',
          }}
        >
          {/* Warning Icon */}
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              backgroundColor: 'rgba(251, 191, 36, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <svg
              width="36"
              height="36"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fbbf24"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
          </Box>

          <Typography
            sx={{
              color: 'white',
              fontSize: '1.5rem',
              fontWeight: 600,
              mb: 2,
            }}
          >
            Complete Your Activation
          </Typography>

          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '1rem',
              mb: 3,
              lineHeight: 1.6,
            }}
          >
            Your account is pending activation. Please check your inbox for an activation link to
            complete your account setup.
          </Typography>

          <Alert
            severity="info"
            sx={{
              mb: 3,
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              color: 'white',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '12px',
              textAlign: 'left',
              '& .MuiAlert-icon': {
                color: '#60a5fa',
              },
            }}
          >
            <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.6 }}>
              Haven't received the email? Check your spam folder or contact us at{' '}
              <Link href="mailto:contact@cacxi.com" sx={{ color: '#a855f7', fontWeight: 600 }}>
                contact@cacxi.com
              </Link>
            </Typography>
          </Alert>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              onClick={() => navigate('/', { replace: true })}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: '12px',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: '#7C8CFF',
                color: '#1a1a2e',
                boxShadow: '0 4px 14px 0 rgba(124, 140, 255, 0.39)',
                '&:hover': {
                  backgroundColor: '#6B7BF0',
                  boxShadow: '0 6px 20px 0 rgba(124, 140, 255, 0.5)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Back to Login
            </Button>

            <Button
              variant="outlined"
              href="https://cacxi.com"
              target="_blank"
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: '12px',
                fontWeight: 600,
                textTransform: 'none',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                color: 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  borderColor: '#a855f7',
                  backgroundColor: 'rgba(168, 85, 247, 0.1)',
                },
              }}
            >
              Book Technical Support
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AuthCallback;
